const toNonEmptyString = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};
const unique = (values) => {
    const seen = new Set();
    for (const value of values ?? []) {
        const normalized = toNonEmptyString(value);
        if (normalized) {
            seen.add(normalized);
        }
    }
    return [...seen];
};
const ensureHistory = (history) => {
    if (!history) {
        return createEmptySearchHistory();
    }
    history.currentPresentation = history.currentPresentation ?? { slideIdsBydocumentId: {} };
    history.currentPresentation.slideIdsBydocumentId = history.currentPresentation.slideIdsBydocumentId ?? {};
    history.presentationLibrary = history.presentationLibrary ?? {
        slideIds: [],
        componentIds: [],
        assetIds: [],
        shapeIds: [],
        layoutIds: [],
    };
    history.presentationLibrary.slideIds = unique(history.presentationLibrary.slideIds);
    history.presentationLibrary.componentIds = unique(history.presentationLibrary.componentIds);
    history.presentationLibrary.assetIds = unique(history.presentationLibrary.assetIds);
    history.presentationLibrary.shapeIds = unique(history.presentationLibrary.shapeIds);
    history.presentationLibrary.layoutIds = unique(history.presentationLibrary.layoutIds);
    return history;
};
export const createEmptySearchHistory = () => ({
    currentPresentation: {
        slideIdsBydocumentId: {},
    },
    presentationLibrary: {
        slideIds: [],
        componentIds: [],
        assetIds: [],
        shapeIds: [],
        layoutIds: [],
    },
});
export const normalizeSearchHistory = (history) => {
    const cloned = history
        ? JSON.parse(JSON.stringify(history))
        : undefined;
    const normalized = ensureHistory(cloned);
    const byDeck = {};
    for (const [documentId, slideIds] of Object.entries(normalized.currentPresentation?.slideIdsBydocumentId ?? {})) {
        const normalizeddocumentId = toNonEmptyString(documentId);
        if (!normalizeddocumentId) {
            continue;
        }
        const deduped = unique(slideIds);
        if (deduped.length > 0) {
            byDeck[normalizeddocumentId] = deduped;
        }
    }
    normalized.currentPresentation = { slideIdsBydocumentId: byDeck };
    normalized.presentationLibrary = {
        slideIds: unique(normalized.presentationLibrary?.slideIds),
        componentIds: unique(normalized.presentationLibrary?.componentIds),
        assetIds: unique(normalized.presentationLibrary?.assetIds),
        shapeIds: unique(normalized.presentationLibrary?.shapeIds),
        layoutIds: unique(normalized.presentationLibrary?.layoutIds),
    };
    return normalized;
};
export const cloneSearchHistory = (history) => normalizeSearchHistory(history);
const upsertDeckSlideIds = (history, documentId, slideIds) => {
    const mutable = ensureHistory(history);
    const current = mutable.currentPresentation.slideIdsBydocumentId[documentId] ?? [];
    mutable.currentPresentation.slideIdsBydocumentId[documentId] = unique([...current, ...slideIds]);
};
export const addCurrentPresentationSearchResults = (history, documentId, results) => {
    const normalizeddocumentId = toNonEmptyString(documentId);
    for (const result of results) {
        const slideId = toNonEmptyString(result.slideId) ?? toNonEmptyString(result.resultId);
        if (!slideId || !slideId.startsWith("slide_")) {
            continue;
        }
        const resultdocumentId = toNonEmptyString(result.presentationId) ?? normalizeddocumentId;
        if (!resultdocumentId) {
            continue;
        }
        upsertDeckSlideIds(history, resultdocumentId, [slideId]);
    }
};
export const addPresentationLibrarySearchResults = (history, results) => {
    const mutable = ensureHistory(history);
    for (const result of results) {
        const resultType = toNonEmptyString(result.resultType)?.toLowerCase();
        const resultId = toNonEmptyString(result.resultId);
        if (!resultType || !resultId) {
            continue;
        }
        switch (resultType) {
            case "slide": {
                const slideId = toNonEmptyString(result.slideId) ?? resultId;
                if (slideId) {
                    mutable.presentationLibrary.slideIds = unique([
                        ...mutable.presentationLibrary.slideIds,
                        slideId,
                    ]);
                }
                break;
            }
            case "component":
                mutable.presentationLibrary.componentIds = unique([
                    ...mutable.presentationLibrary.componentIds,
                    resultId,
                ]);
                break;
            case "asset":
                mutable.presentationLibrary.assetIds = unique([
                    ...mutable.presentationLibrary.assetIds,
                    resultId,
                ]);
                break;
            case "shape": {
                const shapeId = toNonEmptyString(result.shapeId) ?? resultId;
                if (shapeId) {
                    mutable.presentationLibrary.shapeIds = unique([
                        ...mutable.presentationLibrary.shapeIds,
                        shapeId,
                    ]);
                }
                break;
            }
            case "layout": {
                const layoutId = toNonEmptyString(result.layoutId) ?? resultId;
                if (layoutId) {
                    mutable.presentationLibrary.layoutIds = unique([
                        ...mutable.presentationLibrary.layoutIds,
                        layoutId,
                    ]);
                }
                break;
            }
            default:
                break;
        }
    }
};
export const getCurrentPresentationSlideExclusions = (history, documentId) => {
    if (!history || !documentId) {
        return [];
    }
    const normalized = normalizeSearchHistory(history);
    return normalized.currentPresentation?.slideIdsBydocumentId[documentId] ?? [];
};
export const getPresentationLibraryExclusions = (history) => {
    const normalized = normalizeSearchHistory(history);
    return {
        slidesToExclude: normalized.presentationLibrary?.slideIds ?? [],
        componentsToExclude: normalized.presentationLibrary?.componentIds ?? [],
        assetsToExclude: normalized.presentationLibrary?.assetIds ?? [],
        shapesToExclude: normalized.presentationLibrary?.shapeIds ?? [],
        layoutsToExclude: normalized.presentationLibrary?.layoutIds ?? [],
    };
};
const parseToolPayload = (message) => {
    if (message.role !== "tool" || typeof message.content !== "string") {
        return null;
    }
    try {
        const parsed = JSON.parse(message.content);
        return typeof parsed === "object" && parsed !== null ? parsed : null;
    }
    catch {
        return null;
    }
};
const extractSearchPresentationResults = (payload) => {
    if (!Array.isArray(payload.results)) {
        return [];
    }
    return payload.results.filter((item) => typeof item === "object" && item !== null);
};
const extractCurrentPresentationResults = (payload) => {
    if (!Array.isArray(payload.results)) {
        return [];
    }
    return payload.results.filter((item) => typeof item === "object" && item !== null);
};
const extractLibraryResultsFromAgentPayload = (payload) => {
    const extracted = [];
    const topLevelResults = payload.results;
    if (!Array.isArray(topLevelResults)) {
        return extracted;
    }
    for (const item of topLevelResults) {
        if (!item || typeof item !== "object") {
            continue;
        }
        const relevant = item.relevantResults;
        if (!relevant || typeof relevant !== "object") {
            continue;
        }
        const searchResults = relevant.searchResults;
        if (!Array.isArray(searchResults)) {
            continue;
        }
        for (const searchResult of searchResults) {
            if (searchResult && typeof searchResult === "object") {
                extracted.push(searchResult);
            }
        }
    }
    return extracted;
};
export const rebuildSearchHistoryFromMessages = (threads, fallbackdocumentId) => {
    const history = createEmptySearchHistory();
    for (const thread of threads) {
        const toolNameByCallId = new Map();
        for (const message of thread.messages) {
            if (message.role === "assistant" && Array.isArray(message.tool_calls)) {
                for (const toolCall of message.tool_calls) {
                    const toolName = toNonEmptyString(toolCall?.function?.name);
                    const toolCallId = toNonEmptyString(toolCall?.id);
                    if (toolName && toolCallId) {
                        toolNameByCallId.set(toolCallId, toolName);
                    }
                }
                continue;
            }
            if (message.role !== "tool" || !message.tool_call_id) {
                continue;
            }
            const toolName = toolNameByCallId.get(message.tool_call_id);
            if (!toolName) {
                continue;
            }
            const payload = parseToolPayload(message);
            if (!payload) {
                continue;
            }
            if (toolName === "searchPresentation") {
                addPresentationLibrarySearchResults(history, extractSearchPresentationResults(payload));
                continue;
            }
            if (toolName === "currentPresentationSearch") {
                const tooldocumentId = toNonEmptyString(payload.documentId) ?? fallbackdocumentId;
                addCurrentPresentationSearchResults(history, tooldocumentId, extractCurrentPresentationResults(payload));
                continue;
            }
            if (toolName === "presentationLibrarySearchAgent" ||
                toolName === "inPresentationSearch" ||
                toolName === "inPresentationSearchAgent" ||
                toolName === "InPresentationSearchAgent") {
                addPresentationLibrarySearchResults(history, extractLibraryResultsFromAgentPayload(payload));
            }
        }
    }
    return normalizeSearchHistory(history);
};
